import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";

const flattenParams = (params) => {
  const out = { ...params };
  Object.keys(out).forEach((k) => {
    if (Array.isArray(out[k])) {
      out[k] = out[k].length ? out[k].join(",") : undefined;
    }
  });
  return out;
};

export const fetchTickets = createAsyncThunk(
  "tickets/fetch",
  async (params = {}, { rejectWithValue }) => {
    try {
      // `append` is a client-only flag handled by the reducer; strip from API params
      const { append, ...rest } = params;
      const { data } = await api.get(`/tickets`, {
        params: flattenParams(rest),
      });
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load tickets"));
    }
  }
);

export const fetchTicket = createAsyncThunk(
  "tickets/fetchOne",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      return data.data.ticket;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load ticket"));
    }
  }
);

export const createTicket = createAsyncThunk(
  "tickets/create",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/tickets`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data.ticket;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to create ticket"));
    }
  }
);

export const updateTicketMeta = createAsyncThunk(
  "tickets/updateMeta",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, payload);
      return data.data.ticket;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to update ticket"));
    }
  }
);

export const deleteTicket = createAsyncThunk(
  "tickets/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/tickets/${id}`);
      return data.data.ticket;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to delete ticket"));
    }
  }
);

export const restoreTicket = createAsyncThunk(
  "tickets/restore",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/tickets/${id}/restore`);
      return data.data.ticket;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to restore ticket"));
    }
  }
);

export const addUpdate = createAsyncThunk(
  "tickets/addUpdate",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/tickets/${id}/updates`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data.ticket;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to add update"));
    }
  }
);

export const editUpdate = createAsyncThunk(
  "tickets/editUpdate",
  async ({ id, updateId, formData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(
        `/tickets/${id}/updates/${updateId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.data.ticket;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to save update"));
    }
  }
);

export const deleteUpdate = createAsyncThunk(
  "tickets/deleteUpdate",
  async ({ id, updateId }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/tickets/${id}/updates/${updateId}`);
      return data.data.ticket;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to delete update"));
    }
  }
);

export const fetchHeadings = createAsyncThunk(
  "tickets/headings",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/tickets/headings`);
      return data.data.items;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load headings"));
    }
  }
);

export const fetchResolvedByOptions = createAsyncThunk(
  "tickets/resolvedBy",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/tickets/resolved-by`);
      return data.data.items;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load options"));
    }
  }
);

export const fetchAdminUsersForTickets = createAsyncThunk(
  "tickets/adminUsers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/tickets/admin-users`);
      return data.data.items;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load admin users"));
    }
  }
);

export const addHeading = createAsyncThunk(
  "tickets/addHeading",
  async (name, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/tickets/headings`, { name });
      return data.data.item;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to add heading"));
    }
  }
);

export const removeHeading = createAsyncThunk(
  "tickets/removeHeading",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/tickets/headings/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to remove heading"));
    }
  }
);

export const addResolvedByOption = createAsyncThunk(
  "tickets/addResolvedBy",
  async (name, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/tickets/resolved-by`, { name });
      return data.data.item;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to add option"));
    }
  }
);

export const removeResolvedByOption = createAsyncThunk(
  "tickets/removeResolvedBy",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/tickets/resolved-by/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to remove option"));
    }
  }
);

const initialState = {
  items: [],
  pagination: { page: 1, limit: 25, total: 0, pages: 0 },
  query: {
    page: 1,
    limit: 25,
    status: [
      "open",
      "ongoing",
      "waiting_user_feedback",
      "waiting_material",
      "waiting_service",
      "removed",
    ],
    search: "",
    priority: "",
    imei: "",
    heading: "",
    company: "",
    companyId: "",
    responsibleId: "",
    resolvedBy: "",
    contact: "",
    dateFrom: "",
    dateTo: "",
    includeDeleted: false,
  },
  current: null,
  loading: false,
  loadingCurrent: false,
  saving: false,
  uploading: false,
  headings: [],
  resolvedByOptions: [],
  adminUsers: [],
  statuses: [
    "open",
    "ongoing",
    "waiting_user_feedback",
    "waiting_material",
    "waiting_service",
    "resolved",
    "removed",
  ],
  error: null,
};

const replaceInItems = (state, ticket) => {
  const idx = state.items.findIndex((t) => t._id === ticket._id);
  if (idx >= 0) state.items[idx] = ticket;
};

const slice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    setQuery(state, { payload }) {
      state.query = { ...state.query, ...payload };
    },
    clearError(state) {
      state.error = null;
    },
    clearCurrent(state) {
      state.current = null;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchTickets.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchTickets.fulfilled, (s, a) => {
        s.loading = false;
        if (a.meta.arg?.append) {
          // Mobile infinite scroll — append new page's items, de-dupe by _id.
          const existing = new Set(s.items.map((it) => it._id));
          const fresh = a.payload.items.filter(
            (it) => !existing.has(it._id)
          );
          s.items = [...s.items, ...fresh];
        } else {
          s.items = a.payload.items;
        }
        s.pagination = a.payload.pagination;
      })
      .addCase(fetchTickets.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(fetchTicket.pending, (s) => {
        s.loadingCurrent = true;
      })
      .addCase(fetchTicket.fulfilled, (s, a) => {
        s.loadingCurrent = false;
        s.current = a.payload;
        replaceInItems(s, a.payload);
      })
      .addCase(fetchTicket.rejected, (s, a) => {
        s.loadingCurrent = false;
        s.error = a.payload;
      })
      .addCase(createTicket.pending, (s) => {
        s.saving = true;
        s.uploading = true;
      })
      .addCase(createTicket.fulfilled, (s, a) => {
        s.saving = false;
        s.uploading = false;
        s.items = [a.payload, ...s.items];
      })
      .addCase(createTicket.rejected, (s, a) => {
        s.saving = false;
        s.uploading = false;
        s.error = a.payload;
      })
      .addCase(updateTicketMeta.fulfilled, (s, a) => {
        replaceInItems(s, a.payload);
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(updateTicketMeta.rejected, (s, a) => {
        s.error = a.payload;
      })
      .addCase(deleteTicket.fulfilled, (s, a) => {
        if (s.query.includeDeleted) {
          replaceInItems(s, a.payload);
        } else {
          s.items = s.items.filter((t) => t._id !== a.payload._id);
        }
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(restoreTicket.fulfilled, (s, a) => {
        replaceInItems(s, a.payload);
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(addUpdate.pending, (s) => {
        s.saving = true;
        s.uploading = true;
      })
      .addCase(addUpdate.fulfilled, (s, a) => {
        s.saving = false;
        s.uploading = false;
        replaceInItems(s, a.payload);
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(addUpdate.rejected, (s, a) => {
        s.saving = false;
        s.uploading = false;
        s.error = a.payload;
      })
      .addCase(editUpdate.fulfilled, (s, a) => {
        replaceInItems(s, a.payload);
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(deleteUpdate.fulfilled, (s, a) => {
        replaceInItems(s, a.payload);
        if (s.current?._id === a.payload._id) s.current = a.payload;
      })
      .addCase(fetchHeadings.fulfilled, (s, a) => {
        s.headings = a.payload;
      })
      .addCase(fetchResolvedByOptions.fulfilled, (s, a) => {
        s.resolvedByOptions = a.payload;
      })
      .addCase(fetchAdminUsersForTickets.fulfilled, (s, a) => {
        s.adminUsers = a.payload;
      })
      .addCase(addHeading.fulfilled, (s, a) => {
        s.headings = [...s.headings, a.payload].sort((x, y) =>
          x.name.localeCompare(y.name)
        );
      })
      .addCase(removeHeading.fulfilled, (s, a) => {
        s.headings = s.headings.filter((h) => h._id !== a.payload);
      })
      .addCase(addResolvedByOption.fulfilled, (s, a) => {
        s.resolvedByOptions = [...s.resolvedByOptions, a.payload].sort((x, y) =>
          x.name.localeCompare(y.name)
        );
      })
      .addCase(removeResolvedByOption.fulfilled, (s, a) => {
        s.resolvedByOptions = s.resolvedByOptions.filter(
          (o) => o._id !== a.payload
        );
      });
  },
});

export const { setQuery, clearError, clearCurrent } = slice.actions;
export default slice.reducer;
